import { useEffect, useState, useCallback } from "react";
import { Plus, KeyRound, Ban, CheckCircle2 } from "lucide-react";
import moment from "moment";
import {
  getPartnerApplications,
  rotatePartnerApplicationKey,
  revokePartnerApplication,
} from "../../api/partnerApplications";
import {
  IPartnerApplication,
  IPartnerApplicationCreated,
} from "../../models/IPartnerApplication";
import { useAppDispatch } from "../../store/store";
import { setGeneralAlertMessage } from "../../store/slices/generalSlice";
import PartnerApplicationDialog from "../PartnerApplicationDialog";
import ShowApiKeyDialog from "../ShowApiKeyDialog";
import { Button, Card, ConfirmDialog, EmptyState, Loader, Tag } from "../../ui";
import "./styles.scss";

type Props = {
  canManage: boolean;
};

type PendingAction = { kind: "rotate" | "revoke"; partner: IPartnerApplication; inactive: boolean };

const PartnerApplicationsManagement = ({ canManage }: Props) => {
  const dispatch = useAppDispatch();
  const [partners, setPartners] = useState<IPartnerApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [revealKey, setRevealKey] = useState<{ name: string; key: string; isRotation: boolean } | null>(null);
  // The rotate / revoke waiting for confirmation (kept while the dialog animates out)
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const fetchPartners = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getPartnerApplications();
      setPartners(data || []);
    } catch (err: any) {
      dispatch(setGeneralAlertMessage({ message: err?.message || "Failed to load partner applications", type: "error" }));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  const handleCreateClose = (created?: IPartnerApplicationCreated) => {
    setCreateDialogOpen(false);
    if (created) {
      setRevealKey({ name: created.partner.name, key: created.plainTextKey, isRotation: false });
      fetchPartners();
    }
  };

  const askToConfirm = (kind: PendingAction["kind"], partner: IPartnerApplication, inactive: boolean) => {
    setPendingAction({ kind, partner, inactive });
    setIsConfirmOpen(true);
  };

  const handleRotate = async (partner: IPartnerApplication) => {
    setBusyId(partner.id);
    try {
      const { data } = await rotatePartnerApplicationKey(partner.id);
      setRevealKey({ name: data.partner.name, key: data.plainTextKey, isRotation: true });
      await fetchPartners();
    } catch (err: any) {
      dispatch(setGeneralAlertMessage({ message: err?.message || "Failed to rotate API key", type: "error" }));
    } finally {
      setBusyId(null);
    }
  };

  const handleRevoke = async (partner: IPartnerApplication) => {
    setBusyId(partner.id);
    try {
      await revokePartnerApplication(partner.id);
      dispatch(setGeneralAlertMessage({ message: `${partner.name} revoked`, type: "success" }));
      await fetchPartners();
    } catch (err: any) {
      dispatch(setGeneralAlertMessage({ message: err?.message || "Failed to revoke partner", type: "error" }));
    } finally {
      setBusyId(null);
    }
  };

  // Like the old browser confirm: the question closes, then the action runs (the row's buttons show it's busy)
  const runPendingAction = () => {
    if (!pendingAction) return;
    setIsConfirmOpen(false);
    if (pendingAction.kind === "rotate") handleRotate(pendingAction.partner);
    else handleRevoke(pendingAction.partner);
  };

  return (
    <Card
      className="partner-applications-management"
      title="Partner Applications"
      subtitle={`${partners.length} ${partners.length === 1 ? "partner" : "partners"}`}
      padding="none"
      actions={
        canManage && (
          <Button icon={<Plus size={16} />} onClick={() => setCreateDialogOpen(true)}>
            New Partner
          </Button>
        )
      }
    >
      <p className="section-description">
        Third-party applications that can reserve courts on behalf of their users via the partner API
        (<code>X-API-Key</code> header). Keys are stored as SHA-256 hashes — they cannot be retrieved after
        creation, only rotated.
      </p>

      {loading && partners.length === 0 ? (
        <Loader />
      ) : partners.length === 0 ? (
        <EmptyState
          icon={<KeyRound />}
          title="No partner applications yet."
          description={
            canManage && (
              <>
                Click <strong>New Partner</strong> to issue an API key.
              </>
            )
          }
        />
      ) : (
        <div className="partner-list">
          {partners.map((partner) => {
            const inactive = !partner.isActive || !!partner.revokedAt;
            return (
              <div key={partner.id} className={`partner-card ${inactive ? "revoked" : ""}`}>
                <div className="partner-main">
                  <div className="partner-header">
                    <h4>{partner.name}</h4>
                    {inactive ? (
                      <Tag tone="danger" icon={<Ban />}>Revoked</Tag>
                    ) : (
                      <Tag tone="ok" icon={<CheckCircle2 />}>Active</Tag>
                    )}
                  </div>
                  {partner.description && <p className="description">{partner.description}</p>}
                  <div className="partner-meta">
                    <span><strong>Key:</strong> <code>{partner.keyPrefix}…</code></span>
                    {partner.contactEmail && <span><strong>Contact:</strong> {partner.contactEmail}</span>}
                    <span>
                      <strong>Last used:</strong>{" "}
                      {partner.lastUsedAt ? moment(partner.lastUsedAt).fromNow() : "never"}
                    </span>
                    <span><strong>Created:</strong> {moment(partner.createdAt).format("DD MMM YYYY")}</span>
                    {partner.revokedAt && (
                      <span><strong>Revoked:</strong> {moment(partner.revokedAt).format("DD MMM YYYY")}</span>
                    )}
                  </div>
                </div>
                {canManage && (
                  <div className="partner-actions">
                    <Button
                      size="sm"
                      variant="tint"
                      icon={<KeyRound size={14} />}
                      onClick={() => askToConfirm("rotate", partner, inactive)}
                      disabled={busyId === partner.id}
                    >
                      {inactive ? "Reactivate (new key)" : "Rotate Key"}
                    </Button>
                    {!inactive && (
                      <Button
                        size="sm"
                        variant="danger-tint"
                        icon={<Ban size={14} />}
                        onClick={() => askToConfirm("revoke", partner, inactive)}
                        disabled={busyId === partner.id}
                      >
                        Revoke
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <PartnerApplicationDialog isOpen={createDialogOpen} onClose={handleCreateClose} />

      <ConfirmDialog
        open={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={runPendingAction}
        tone={pendingAction?.kind === "revoke" ? "danger" : "primary"}
        title={
          pendingAction?.kind === "revoke"
            ? `Revoke "${pendingAction.partner.name}"?`
            : `Rotate the API key for "${pendingAction?.partner.name ?? ""}"?`
        }
        message={
          pendingAction?.kind === "revoke"
            ? "Their key will stop working immediately and existing reservations will remain."
            : "The current key will stop working immediately."
        }
        confirmLabel={
          pendingAction?.kind === "revoke" ? "Revoke" : pendingAction?.inactive ? "Reactivate (new key)" : "Rotate Key"
        }
      />

      {revealKey && (
        <ShowApiKeyDialog
          isOpen={!!revealKey}
          partnerName={revealKey.name}
          apiKey={revealKey.key}
          isRotation={revealKey.isRotation}
          onClose={() => setRevealKey(null)}
        />
      )}
    </Card>
  );
};

export default PartnerApplicationsManagement;
